import Chart from "react-apexcharts";
import { ApexOptions } from "apexcharts";
import { FiInfo } from "react-icons/fi";

interface MonthlySalesChartProps {
  title: string;
  data: number[];
  categories: string[];
  tooltipText?: string;
}

const Tooltip = ({ text }: { text: string }) => (
  <div className="group relative inline-block">
    <button className="flex items-center justify-center rounded-lg p-2 hover:bg-gray-100 dark:hover:bg-gray-800">
      <FiInfo className="text-gray-500 dark:text-gray-400" />
    </button>
    <div className="pointer-events-none absolute bottom-full right-0 mb-2 hidden w-48 rounded bg-gray-800 p-2 text-center text-xs text-white opacity-0 transition-opacity group-hover:block group-hover:opacity-100 z-50">
      {text}
      <div className="absolute right-3 top-full -ml-1 h-0 w-0 border-4 border-solid border-transparent border-t-gray-800"></div>
    </div>
  </div>
);

export default function MonthlySalesChart({
  title,
  data,
  categories,
  tooltipText,
}: MonthlySalesChartProps) {
  // Calcular média dos dados para a linha
  const average =
    data.length > 0
      ? Math.round(data.reduce((sum, val) => sum + val, 0) / data.length)
      : 0;

  const lineData = data.map(() => average);

  // Paleta de cores variadas para as barras
  const barColors = [
    "#f97316", // Laranja
    "#3b82f6", // Azul
    "#10b981", // Verde
    "#85368c", // Roxo
    "#ec4899", // Rosa
    "#06b6d4", // Ciano
    "#f59e0b", // Amarelo
    "#14b8a6", // Turquesa
    "#6366f1", // Índigo
    "#ef4444", // Vermelho
    "#84cc16", // Verde lima
    "#f43f5e", // Rosa escuro
  ];

  const options: ApexOptions = {
    colors: [...barColors, "#9333ea"], // Array de cores para as barras + roxo para linha
    chart: {
      fontFamily: "Outfit, sans-serif",
      toolbar: {
        show: false,
      },
    },
    plotOptions: {
      bar: {
        horizontal: false,
        columnWidth: "39%",
        borderRadius: 5,
        borderRadiusApplication: "end",
        distributed: true, // Permite cores diferentes por barra
      },
    },
    dataLabels: {
      enabled: false,
    },
    stroke: {
      curve: "smooth",
      width: [0, 3], // 0 para barras, 3 para linha
      colors: ["transparent", "#9333ea"], // Transparente para barras, roxo para linha
    },
    xaxis: {
      categories: categories,
      axisBorder: {
        show: false,
      },
      axisTicks: {
        show: false,
      },
    },
    legend: {
      show: true,
      position: "top",
      horizontalAlign: "left",
      fontFamily: "Outfit",
    },
    yaxis: {
      title: {
        text: undefined,
      },
    },
    grid: {
      yaxis: {
        lines: {
          show: true,
        },
      },
    },
    fill: {
      opacity: 1,
    },
    tooltip: {
      x: {
        show: false,
      },
      y: {
        formatter: (val: number, opts: { seriesIndex: number }) => {
          if (opts.seriesIndex === 0) {
            return `${val} kcal`;
          }
          return `Média: ${val} kcal`;
        },
      },
    },
  };

  const series = [
    {
      name: title,
      type: "column",
      data: data,
    },
    {
      name: "Média",
      type: "line",
      data: lineData,
    },
  ];

  return (
    <div className="flex h-full flex-col rounded-2xl border border-gray-200 bg-white dark:border-gray-800 dark:bg-white/[0.03]">
      <div className="border-b border-stroke px-6 pt-6 pb-4 dark:border-strokedark">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold text-gray-800 dark:text-white/90">
            {title}
          </h3>
          {tooltipText && (
            <div className="relative inline-block text-left">
              <Tooltip text={tooltipText} />
            </div>
          )}
        </div>
      </div>

      <div className="flex flex-1 items-center justify-center overflow-hidden custom-scrollbar p-6">
        <div className="w-full overflow-hidden">
          <Chart options={options} series={series} type="line" height={280} />
        </div>
      </div>
    </div>
  );
}
